import { useMemo } from "react";
import type { CreateParameterRequest } from "@opus-dt/shared-types";
import { getDraftStore, useDraftStore } from "@/features/lines/draft/useLineDraftState";
import { isDraftLineId } from "@/features/lines/canvas/draft-canvas-adapter";
import {
  useDeleteMachine,
  useUpdateMachine,
  useCreateParameter,
  useDeleteParameter,
  useUpdateParameter,
  useParameters,
  useCompatibility,
  useUpsertCompatibility,
} from "@/features/line-editor/hooks";
import { useLineMachines } from "@/features/lines/hooks";
import { useAuthStore } from "@/stores/auth.store";
import type { CompatibilityRow, ParameterDetail } from "@/types/line-editor.types";
import type { MachineDetail as EditMachineDetail } from "@/types/line-editor.types";
import type {
  BasicPatch,
  MachineSidebarBridge,
  NewParameterInput,
  ParameterPatch,
  SensorLinkRow,
} from "../bridge-types";
import {
  draftAddParameter,
  draftLinkSensor,
  draftSetCompatibility,
  draftUpdateParameter,
} from "./draft-bridge-actions";
import {
  selectCompatibilities,
  selectMachine,
  selectParameters,
  selectSensorLinks,
} from "./bridge-selectors";

export function useMachineSidebarBridge(
  machineId: string | null | undefined,
  lineId: string
): MachineSidebarBridge {
  const isDraft = isDraftLineId(lineId);
  const me = useAuthStore((s) => s.user);
  const readOnly = me?.role === "OPERADOR";

  const draftMachineMap = useDraftStore((s) => s.machines);
  const draftMachine = machineId ? draftMachineMap[machineId] : undefined;

  const lineMachinesQuery = useLineMachines(isDraft ? "" : lineId);
  const { data: editMachines = [] } = lineMachinesQuery;
  const editMachine =
    !isDraft && machineId ? editMachines.find((m) => m.id === machineId) : undefined;

  const parametersQuery = useParameters(isDraft || !machineId ? "" : machineId);
  const { data: editParameters = [] } = parametersQuery;
  const compatibilityQuery = useCompatibility(isDraft ? "" : lineId);
  const { data: editCompatibility = [] } = compatibilityQuery;

  // Por domínio (não agregado): cada tab consumidora só deve tratar como erro
  // a query que realmente alimenta ela, senão uma falha em compatibilidade acenderia erro
  // na aba de parâmetros (e vice-versa). Em draft as queries ficam `enabled:false` (isError
  // sempre false), então só reflete erro real do modo edit.
  const parametersIsError = parametersQuery.isError;
  const parametersError = parametersQuery.error;
  const refetchParameters = () => void parametersQuery.refetch();
  const compatibilitiesIsError = compatibilityQuery.isError;
  const compatibilitiesError = compatibilityQuery.error;
  const refetchCompatibilities = () => void compatibilityQuery.refetch();

  const updateMachineMut = useUpdateMachine(lineId, machineId ?? "");
  const deleteMachineMut = useDeleteMachine(lineId);
  const createParameterMut = useCreateParameter(machineId ?? "", lineId);
  const updateParameterMut = useUpdateParameter(machineId ?? "", lineId);
  const deleteParameterMut = useDeleteParameter(machineId ?? "", lineId);
  const upsertCompatMut = useUpsertCompatibility(lineId);

  const machine: EditMachineDetail | undefined = useMemo(
    () => selectMachine(isDraft, draftMachine, editMachine, lineId),
    [isDraft, draftMachine, editMachine, lineId]
  );
  const parameters: ParameterDetail[] = useMemo(
    () => selectParameters(isDraft, draftMachine, editParameters),
    [isDraft, draftMachine, editParameters]
  );
  const sensorLinks: SensorLinkRow[] = useMemo(
    () => selectSensorLinks(isDraft, draftMachine),
    [isDraft, draftMachine]
  );
  const compatibilities: CompatibilityRow[] = useMemo(
    () => selectCompatibilities(isDraft, draftMachine, editCompatibility, machineId),
    [isDraft, draftMachine, editCompatibility, machineId]
  );

  const bridge: MachineSidebarBridge = {
    mode: isDraft ? "draft" : "edit",
    machine,
    readOnly,
    parameters,
    sensorLinks,
    compatibilities,
    parametersIsError,
    parametersError,
    refetchParameters,
    compatibilitiesIsError,
    compatibilitiesError,
    refetchCompatibilities,

    updateBasic: async (patch: BasicPatch) => {
      if (isDraft) {
        if (!machineId) throw new Error("Sem máquina selecionada");
        getDraftStore()
          .getState()
          .updateMachineBasic(machineId, {
            name: patch.name,
            code: patch.code,
            type: patch.type,
            requiresOperator: patch.requiresOperator,
            stoppedCostPerHour: patch.stoppedCostPerHour ?? undefined,
            // Spec 080 — o rascunho passa a carregar o modelo 3D. Antes ele parava aqui: a barra
            // lateral deixava escolher, relatava sucesso, e a máquina nascia como bloco genérico.
            ...(patch.model3dId !== undefined ? { model3dId: patch.model3dId } : {}),
          });
        return;
      }
      await updateMachineMut.mutateAsync({
        name: patch.name,
        code: patch.code,
        type: patch.type,
        requiresOperator: patch.requiresOperator,
        // null = limpar o custo (schema aceita nullable desde — review fix #4)
        stoppedCostPerHour: patch.stoppedCostPerHour,
        // Spec 078 — undefined mantém; null desassocia; id associa (a API valida a organização).
        ...(patch.model3dId !== undefined ? { model3dId: patch.model3dId } : {}),
      });
    },

    addParameter: async (input: NewParameterInput) => {
      if (isDraft) {
        if (!machineId) throw new Error("Sem máquina selecionada");
        return draftAddParameter(machineId, input);
      }
      if (!machineId) throw new Error("Sem máquina");
      const created = await createParameterMut.mutateAsync(input as CreateParameterRequest);
      return created.id;
    },

    updateParameter: async (paramId: string, patch: ParameterPatch) => {
      if (isDraft) {
        if (!machineId) throw new Error("Sem máquina");
        draftUpdateParameter(machineId, paramId, patch);
        return;
      }
      await updateParameterMut.mutateAsync({
        id: paramId,
        data: patch as Partial<CreateParameterRequest>,
      });
    },

    removeParameter: async (paramId: string) => {
      if (isDraft) {
        if (!machineId) throw new Error("Sem máquina");
        getDraftStore().getState().removeParameter(machineId, paramId);
        return;
      }
      await deleteParameterMut.mutateAsync(paramId);
    },

    linkSensor: async (parameterId: string, sensorId: string) => {
      if (isDraft) {
        if (!machineId) throw new Error("Sem máquina");
        draftLinkSensor(machineId, parameterId, sensorId);
        await Promise.resolve();
        return;
      }
      throw new Error("Em edit, use LinkSensorDialog próprio.");
    },

    unlinkSensor: async (sensorLinkId: string) => {
      if (isDraft) {
        if (!machineId) throw new Error("Sem máquina");
        getDraftStore().getState().removeSensorLink(machineId, sensorLinkId);
        await Promise.resolve();
        return;
      }
      throw new Error("Em edit, use mutation própria do SensorsTab.");
    },

    setCompatibility: async (productId, enabled, simProfile) => {
      if (isDraft) {
        if (!machineId) throw new Error("Sem máquina");
        draftSetCompatibility(machineId, productId, enabled, simProfile);
        return;
      }
      await upsertCompatMut.mutateAsync({
        // `simProfile` omitido não vai no body: o service preserva o perfil autorado (tri-state).
        entries: [
          {
            machineId: machineId ?? "",
            productId,
            enabled,
            ...(simProfile !== undefined && { simProfile }),
          },
        ],
      });
    },

    removeMachine: async () => {
      if (!machineId) return;
      if (isDraft) {
        getDraftStore().getState().removeMachine(machineId);
        return;
      }
      await deleteMachineMut.mutateAsync(machineId);
    },
  };

  return bridge;
}
